// W13 — 方案选择: 家庭版(全能生活助理) / 商用版(含家庭版全部功能+商业套件)
// 场景分野而非高低配; 试用全程可选, 到期/宽限期软着陆也落到这里。
import { useState, useEffect } from 'react';
import api from '../api';
import { useT } from '../i18n';
import { Home, Briefcase, Check, ArrowUpCircle, Loader2, Sparkles } from 'lucide-react';
import { fmtCents } from '../utils/money';

const FAMILY_FEATURES = [
  'plan_sel.family_feat_chat', 'plan_sel.family_feat_today', 'plan_sel.family_feat_planner',
  'plan_sel.family_feat_recipe', 'plan_sel.family_feat_map', 'plan_sel.family_feat_stream',
];
const BIZ_FEATURES = [
  'plan_sel.biz_feat_radar', 'plan_sel.biz_feat_monitor', 'plan_sel.biz_feat_campaigns',
  'plan_sel.biz_feat_leads', 'plan_sel.biz_feat_kb', 'plan_sel.biz_feat_assistants', 'plan_sel.biz_feat_api',
];

function PlanCard({ icon: Icon, title, subtitle, priceText, oneTime, features, includesFamily, action, current, highlight, t, children }) {
  return (
    <div
      className="rounded-xl border p-5 flex flex-col gap-3 relative"
      style={{
        borderColor: highlight ? 'var(--brand)' : 'var(--border)',
        background: 'var(--bg-card)',
        boxShadow: 'var(--shadow-card)',
        outline: highlight ? '1px solid var(--brand-ring)' : 'none',
      }}
    >
      {current && (
        <span className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'var(--bg-active)', color: 'var(--text-brand)' }}>
          {t('plan_sel.current')}
        </span>
      )}
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-lg inline-flex items-center justify-center" style={{ background: 'var(--bg-active)' }}>
          <Icon size={18} style={{ color: 'var(--text-brand)' }} />
        </span>
        <div>
          <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{subtitle}</p>
        </div>
      </div>
      {priceText && <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{priceText}</div>}
      {oneTime && (
        <span className="inline-flex w-fit px-2 py-0.5 rounded-full text-xs font-semibold"
          style={{ background: 'rgba(59,130,246,0.10)', color: 'var(--text-brand)', border: '1px solid rgba(59,130,246,0.25)' }}>
          {t('plan_sel.one_time')}
        </span>
      )}
      {children}
      {includesFamily && (
        <p className="text-xs inline-flex items-center gap-1 font-medium" style={{ color: 'var(--text-brand)' }}>
          <Sparkles size={12} /> {t('plan_sel.includes_family')}
        </p>
      )}
      <ul className="flex flex-col gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
        {features.map(f => (
          <li key={f} className="flex items-start gap-2">
            <Check size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--text-brand)' }} />
            <span>{t(f)}</span>
          </li>
        ))}
      </ul>
      {action}
    </div>
  );
}

export default function PlanSelector({ walletData }) {
  const t = useT();
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api.get('/wallet/plan-pricing').then(r => setPricing(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const state = params.get('plan');
    if (state === 'activated') setNotice({ kind: 'ok', msg: t('plan_sel.activated') });
    else if (state === 'cancel') setNotice({ kind: 'warn', msg: t('plan_sel.cancelled') });
    else if (state === 'expired') setNotice({ kind: 'warn', msg: t('plan_sel.expired_hint') });
    // 侧边栏 #plans 锚点直达 (react-router 不自动滚 hash)
    if (window.location.hash === '#plans') {
      setTimeout(() => document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }, []);

  const plan = walletData?.plan?.name || null; // trial/family/business
  // W16: 统一 "$" + 整数千分位 ($700 / $2,900), 无币种码; 实际币种由 admin 激活币种决定
  const fmt = fmtCents;

  const checkout = async (p) => {
    if (busy) return;
    setBusy(p);
    try {
      const r = await api.post('/wallet/plan-checkout', { plan: p });
      if (r.data?.checkout_url) { window.location.href = r.data.checkout_url; return; }
      setBusy('');
    } catch (e) {
      setNotice({ kind: 'warn', msg: e?.response?.data?.error || t('plan_sel.checkout_failed') });
      setBusy('');
    }
  };

  const btn = (p, labelKey, primary) => (
    <button
      onClick={() => checkout(p)}
      disabled={!!busy}
      className={primary ? 'btn-primary' : 'btn-secondary'}
      style={{ marginTop: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
    >
      {busy === p ? <Loader2 size={15} className="spin" /> : null}
      {t(labelKey)}
    </button>
  );

  const isTrial = plan === 'trial' || !plan;
  const isFamily = plan === 'family';
  const isBusiness = plan === 'business' || plan === 'enterprise';

  return (
    <section id="plans" className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{t('plan_sel.title')}</h2>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('plan_sel.subtitle')}</p>
      </div>
      {notice && (
        <div
          className="rounded-lg px-4 py-2.5 text-sm"
          style={{
            background: notice.kind === 'ok' ? 'rgba(46,204,113,0.10)' : 'rgba(234,179,8,0.12)',
            color: 'var(--text-primary)',
            border: `1px solid ${notice.kind === 'ok' ? 'rgba(46,204,113,0.4)' : 'rgba(234,179,8,0.5)'}`,
          }}
        >
          {notice.msg}
        </div>
      )}
      {!pricing?.stripe_configured && (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('plan_sel.not_configured')}</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlanCard
          icon={Home}
          title={t('plan_sel.family')}
          subtitle={t('plan_sel.family_sub')}
          priceText={pricing ? fmt(pricing.plans?.family?.price_cents) : ''}
          oneTime
          features={FAMILY_FEATURES}
          current={isFamily}
          highlight={isFamily}
          t={t}
          action={isFamily
            ? null
            : isBusiness
              ? null
              : btn('family', 'plan_sel.choose_family', false)}
        >
          {pricing && (
            <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
              {t('plan_sel.upgrade_price', { price: fmt(pricing.plans?.business_upgrade?.price_cents) })}
            </p>
          )}
        </PlanCard>
        <PlanCard
          icon={Briefcase}
          title={t('plan_sel.business')}
          subtitle={t('plan_sel.business_sub')}
          priceText={pricing ? fmt(isFamily ? pricing.plans?.business_upgrade?.price_cents : pricing.plans?.business?.price_cents) : ''}
          oneTime
          features={BIZ_FEATURES}
          includesFamily
          current={isBusiness}
          highlight={isTrial || isFamily || isBusiness}
          t={t}
          action={isBusiness
            ? null
            : isFamily
              ? btn('business_upgrade', 'plan_sel.upgrade_business', true)
              : btn('business', 'plan_sel.choose_business', true)}
        />
      </div>
      {isFamily && (
        <p className="text-xs inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
          <ArrowUpCircle size={12} /> {t('plan_sel.upgrade_hint')}
        </p>
      )}
    </section>
  );
}
